
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Contact() {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  // SEO Meta Tags
  useEffect(() => {
    document.title = 'İletişim - GüvenTar Sistemleri | Damla Sulama Uzmanları Antalya Serik';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'GüvenTar Sistemleri ile iletişime geçin. Antalya Serik merkezli damla sulama sistemleri kurulumu ve tarımsal sulama çözümleri için uzman ekibimizden teklif alın. Telefon: +90 553 414 52 63');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'iletişim, teklif al, damla sulama, GüvenTar, Antalya, Serik');
    }

    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://www.guventar.com'}/iletisim`);
    }
  }, []);

  const toggleLanguage = () => {
    const newLang = language === 'tr' ? 'en' : 'tr';
    setLanguage(newLang);
    i18n.changeLanguage(newLang);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Form submission logic here
    console.log('Form submitted:', formData);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <header>
        <h1 style={{ position: 'absolute', left: '-9999px' }}>İletişim - GüvenTar Sistemleri</h1>
      </header>

      {/* Language Toggle Button - Hidden on Mobile */}
      <div className="fixed top-4 sm:top-6 right-4 sm:right-6 z-50 hidden sm:block">
        <button
          onClick={toggleLanguage}
          className="group relative flex items-center space-x-2 sm:space-x-3 bg-white/90 backdrop-blur-md text-gray-700 px-4 sm:px-6 py-2 sm:py-3 rounded-full border border-gray-200/50 hover:bg-white hover:shadow-lg transition-all duration-300 cursor-pointer whitespace-nowrap"
        >
          <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-green-800 to-green-900 rounded-full flex items-center justify-center shadow-sm">
            <i className="ri-global-line text-white text-sm sm:text-lg"></i>
          </div>
          <div className="hidden sm:flex flex-col items-start">
            <span className="text-xs text-gray-500 font-medium">{t('language.label')}</span>
            <span className="text-sm font-semibold text-gray-800">
              {t('language.switch')}
            </span>
          </div>
          <span className="sm:hidden text-sm font-semibold text-gray-800">
            {language === 'tr' ? 'EN' : 'TR'}
          </span>
          <i className="ri-arrow-down-s-line text-gray-400 group-hover:text-gray-600 transition-colors hidden sm:block"></i>
        </button>
      </div>

      {/* Phone Call Button */}
      <a
        href="tel:+905534145263"
        className="fixed right-4 sm:right-6 bottom-20 sm:bottom-24 w-12 h-12 sm:w-14 sm:h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 z-40 cursor-pointer"
        title="Hemen Ara"
      >
        <i className="ri-phone-line text-lg sm:text-xl"></i>
      </a>

      {/* WhatsApp Button */}
      <a
        href="https://wa.me/905534145263"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed right-4 sm:right-6 bottom-4 sm:bottom-6 w-12 h-12 sm:w-14 sm:h-14 bg-green-800 hover:bg-green-900 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 z-40 cursor-pointer"
        title="WhatsApp"
      >
        <i className="ri-whatsapp-line text-lg sm:text-xl"></i>
      </a>

      {/* Main Content with Left Margin for Desktop */}
      <div className="lg:ml-80 min-h-screen flex flex-col">
        {/* Hero Section */}
        <section 
          className="relative py-20 sm:py-24 lg:py-32 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(27, 94, 32, 0.8), rgba(27, 94, 32, 0.8)), url('https://readdy.ai/api/search-image?query=Professional%20business%20office%20with%20modern%20communication%20technology%2C%20clean%20workspace%20with%20phones%20and%20computers%2C%20customer%20service%20environment%2C%20green%20and%20white%20color%20scheme%2C%20professional%20consultation%20setting&width=1920&height=600&seq=contact-hero&orientation=landscape')`
          }}
        >
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {t('contact.hero.title')}
            </h2>
            <p className="text-xl sm:text-2xl text-green-100 leading-relaxed">
              {t('contact.hero.subtitle')}
            </p>
          </div>
        </section>

        {/* Contact Content */}
        <div className="flex-1 py-12 sm:py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              {/* Contact Information */}
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {t('contact.info.title')}
                </h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <a 
                      href="https://share.google/1uH7RJbgzVeQGLQLu"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-green-800 rounded-xl flex items-center justify-center flex-shrink-0 hover:scale-110 transition-transform duration-300 cursor-pointer"
                    >
                      <i className="ri-map-pin-line text-white text-xl"></i>
                    </a>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{t('contact.info.address.title')}</h4>
                      <a 
                        href="https://share.google/1uH7RJbgzVeQGLQLu"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-green-800 transition-colors cursor-pointer hover:scale-105 transform duration-300 inline-block"
                      >
                        Cumalı, Antalya Cd. No:9<br />
                        07500 Serik/Antalya
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <a 
                      href="tel:+905534145263"
                      className="w-12 h-12 bg-green-800 rounded-xl flex items-center justify-center flex-shrink-0 hover:scale-110 transition-transform duration-300 cursor-pointer"
                    >
                      <i className="ri-phone-line text-white text-xl"></i>
                    </a>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{t('contact.info.phone.title')}</h4>
                      <a href="tel:+905534145263" className="text-green-800 hover:text-green-900 cursor-pointer hover:scale-105 transform duration-300 inline-block">
                        +90 553 414 52 63
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <a 
                      href="mailto:bilgi@guventar.com"
                      className="w-12 h-12 bg-green-800 rounded-xl flex items-center justify-center flex-shrink-0 hover:scale-110 transition-transform duration-300 cursor-pointer"
                    >
                      <i className="ri-mail-line text-white text-xl"></i>
                    </a>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{t('contact.info.email.title')}</h4>
                      <a href="mailto:bilgi@guventar.com" className="text-green-800 hover:text-green-900 cursor-pointer hover:scale-105 transform duration-300 inline-block">
                        bilgi@guventar.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-green-800 rounded-xl flex items-center justify-center flex-shrink-0 hover:scale-110 transition-transform duration-300">
                      <i className="ri-time-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{t('contact.info.hours.title')}</h4>
                      <p className="text-gray-600">
                        {t('contact.info.hours.weekdays')}<br />
                        {t('contact.info.hours.weekend')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* WhatsApp Contact */}
                <div className="mt-8 p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl border border-green-200 shadow-lg">
                  <div className="flex items-center space-x-4">
                    <a
                      href="https://wa.me/905534145263?text=Merhaba, damla sulama sistemleri hakkında bilgi almak istiyorum."
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-16 h-16 bg-gradient-to-br from-green-800 to-green-900 rounded-2xl flex items-center justify-center hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl"
                    >
                      <i className="ri-whatsapp-line text-white text-2xl"></i>
                    </a>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 mb-2 text-lg">
                        {language === 'tr' ? 'WhatsApp İletişim' : t('contact.whatsapp.title')}
                      </h4>
                      <p className="text-gray-700 text-sm mb-4 leading-relaxed">
                        {language === 'tr' ? 'Hızlı iletişim için WhatsApp üzerinden bize ulaşabilirsiniz. Anında yanıt alın ve projeleriniz hakkında konuşalım.' : t('contact.whatsapp.desc')}
                      </p>
                      <a
                        href="https://wa.me/905534145263?text=Merhaba, damla sulama sistemleri hakkında bilgi almak istiyorum."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center bg-gradient-to-r from-green-800 to-green-900 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-900 hover:to-green-800 transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105 transform shadow-lg hover:shadow-xl"
                      >
                        <i className="ri-whatsapp-line mr-2 text-lg"></i>
                        {language === 'tr' ? 'WhatsApp\'ta Mesaj Gönder' : t('contact.whatsapp.button')}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {t('contact.form.title')}
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.form.name')}
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-800 focus:border-transparent transition-colors text-sm"
                        placeholder={t('contact.form.name.placeholder')}
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.form.phone')}
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-800 focus:border-transparent transition-colors text-sm"
                        placeholder={t('contact.form.phone.placeholder')}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.email')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-800 focus:border-transparent transition-colors text-sm"
                      placeholder={t('contact.form.email.placeholder')}
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.subject')}
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-800 focus:border-transparent transition-colors text-sm"
                    >
                      <option value="">{t('contact.form.subject.placeholder')}</option>
                      <option value="damla-sulama">{t('contact.form.subject.drip')}</option>
                      <option value="yagmurlama">{t('contact.form.subject.sprinkler')}</option>
                      <option value="otomasyon">{t('contact.form.subject.automation')}</option>
                      <option value="bakim">{t('contact.form.subject.maintenance')}</option>
                      <option value="diger">{t('contact.form.subject.other')}</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.message')}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-800 focus:border-transparent transition-colors resize-none text-sm"
                      placeholder={t('contact.form.message.placeholder')}
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.message.length}/500 {t('contact.form.message.limit')}
                    </p>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-green-800 text-white py-3 px-6 rounded-xl font-semibold hover:bg-green-900 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-send-plane-line mr-2"></i>
                    {t('contact.form.submit')}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Map Section */}
        <section className="py-12 sm:py-16 bg-gradient-to-br from-green-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {t('contact.map.title')}
              </h3>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {language === 'tr' ? 'Antalya Serik merkezli ofisimizi ziyaret edin. Damla sulama sistemleri konusunda uzman ekibimizle yüz yüze görüşme imkanı.' : 'Visit our office based in Antalya Serik. Face-to-face meeting opportunity with our expert team on drip irrigation systems.'}
              </p>
              <div className="mt-6">
                <a
                  href="https://share.google/1uH7RJbgzVeQGLQLu"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-green-800 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-900 transition-colors cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
                >
                  <i className="ri-navigation-line mr-2"></i>
                  {language === 'tr' ? 'Yol Tarifi Al' : 'Get Directions'}
                </a>
              </div>
            </div>
            
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1896.682715598109!2d31.06079215205635!3d36.91839114472913!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14c37d2909c1f077%3A0xf9377c2010a5b890!2sG%C3%BCventar%20Sulama%20Sistemleri!5e0!3m2!1str!2str!4v1762246108040!5m2!1str!2str"
                  width="100%"
                  height="500"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="GüvenTar Sistemleri Konum - Cumalı, Antalya Cd. No:9 Serik/Antalya"
                ></iframe>
              </div>

              {/* Map Overlay Info */}
              <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/50 max-w-xs">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="w-8 h-8 bg-green-800 rounded-full flex items-center justify-center">
                    <i className="ri-building-line text-white text-sm"></i>
                  </div>
                  <span className="font-bold text-gray-900">GüvenTar Sistemleri</span>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Cumalı, Antalya Cd. No:9<br />
                  07500 Serik/Antalya
                </p>
                <div className="flex space-x-2">
                  <a
                    href="tel:+905534145263"
                    className="flex-1 bg-blue-600 text-white text-xs px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer text-center whitespace-nowrap"
                  >
                    <i className="ri-phone-line mr-1"></i>
                    {language === 'tr' ? 'Ara' : 'Call'}
                  </a>
                  <a
                    href="https://share.google/1uH7RJbgzVeQGLQLu"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-green-800 text-white text-xs px-3 py-2 rounded-lg hover:bg-green-900 transition-colors cursor-pointer text-center whitespace-nowrap"
                  >
                    <i className="ri-navigation-line mr-1"></i>
                    {language === 'tr' ? 'Yol Tarifi' : 'Directions'}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
}